import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getAutoBlogConfig, updateAutoBlogConfig, autoBlogRunNow, educationStatus, educationToggle, educationGenerate, educationSeed, toolsEngineStatus, toolsEngineToggle, toolsEngineRunNow, toolBuilderStatus, toolBuilderToggle, toolBuilderRunNow, skillEngineStatus, skillEngineToggle, skillEngineRunNow, funEngineStatus, funEngineToggle, funEngineRunNow, funEngineBoostAll, fbRepliesStatus, fbRepliesToggle } from "@/lib/api";
import AdminAvenoEngine from "@/pages/admin/AdminAvenoEngine";
import { Zap, Play, Save, Clock, CheckCircle2, XCircle, ExternalLink, Sparkles, BookOpen, Wrench, Hammer, Briefcase, Laugh, MessageCircle } from "lucide-react";

export default function AutoBlog() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => getAutoBlogConfig().then(setCfg).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!cfg) return <p className="text-muted-foreground">Loading…</p>;

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        enabled: cfg.enabled,
        posts_per_day: Number(cfg.posts_per_day),
        hours_utc: (cfg.hours_utc || []).map(Number).filter((h) => h >= 0 && h < 24),
        niches: (typeof cfg.niches === "string" ? cfg.niches.split(",") : cfg.niches).map((s) => String(s).trim()).filter(Boolean),
        include_affiliates: cfg.include_affiliates,
      };
      const next = await updateAutoBlogConfig(patch);
      setCfg(next);
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!window.confirm("Generate and publish a post right now?")) return;
    setRunning(true);
    try {
      const r = await autoBlogRunNow();
      if (r.ok) toast.success(`Published: ${r.title}`);
      else toast.error(r.error || "Generation failed");
      load();
    } catch { toast.error("Run failed"); } finally { setRunning(false); }
  };

  const nichesStr = Array.isArray(cfg.niches) ? cfg.niches.join(", ") : (cfg.niches || "");
  const hoursStr = (cfg.hours_utc || []).join(", ");

  return (
    <div data-testid="admin-auto-blog">
      <div className="flex items-center gap-3 mb-2">
        <Zap className="w-7 h-7 text-primary" />
        <h2 className="font-display text-3xl">Auto Blog</h2>
      </div>
      <p className="text-muted-foreground mb-8 max-w-2xl">The AI writes and publishes fresh blog posts for you at scheduled times each day. Every post naturally includes affiliate links from your Recommended list.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-display text-xl">Enable scheduler</p>
              <p className="text-xs text-muted-foreground mt-1">When off, no auto-posts run.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input data-testid="auto-enabled" type="checkbox" className="sr-only peer" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} />
              <div className="w-11 h-6 bg-secondary peer-checked:bg-primary rounded-full transition-colors relative after:absolute after:left-0.5 after:top-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
            </label>
          </div>

          <div>
            <label className="overline text-muted-foreground block mb-2">Posts per day</label>
            <input data-testid="auto-ppd" type="number" min={1} max={6} value={cfg.posts_per_day || 3} onChange={(e) => set("posts_per_day", e.target.value)} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" />
            <p className="text-xs text-muted-foreground mt-1.5">Change this and save — I&apos;ll spread the hours evenly. Or set custom hours below.</p>
          </div>

          <div>
            <label className="overline text-muted-foreground block mb-2">Hours (UTC, comma-separated)</label>
            <input data-testid="auto-hours" value={hoursStr} onChange={(e) => set("hours_utc", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary font-mono text-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">e.g. <code>6, 14, 22</code> = 6am, 2pm, 10pm UTC.</p>
          </div>

          <div>
            <label className="overline text-muted-foreground block mb-2">Niches (comma-separated)</label>
            <textarea data-testid="auto-niches" rows={3} value={nichesStr} onChange={(e) => set("niches", e.target.value)} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" />
            <p className="text-xs text-muted-foreground mt-1.5">The AI picks fresh topics from these areas each run.</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="auto-aff" type="checkbox" checked={!!cfg.include_affiliates} onChange={(e) => set("include_affiliates", e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" />
            <span className="text-sm">Weave affiliate links from Recommended list into every post</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button data-testid="auto-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-60"><Save className="w-4 h-4" /> Save Settings</button>
            <button data-testid="auto-run-now" onClick={runNow} disabled={running} className="inline-flex items-center gap-2 border border-border px-5 py-2.5 font-semibold hover:border-primary hover:text-primary disabled:opacity-60"><Play className="w-4 h-4" /> {running ? "Publishing…" : "Generate & Publish Now"}</button>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary" />
            <p className="font-display text-xl">Recent auto-runs</p>
          </div>
          {(!cfg.history || cfg.history.length === 0) ? (
            <p className="text-sm text-muted-foreground">Nothing yet. Toggle Enable + Save, or press <em>Generate & Publish Now</em>.</p>
          ) : (
            <ul className="divide-y divide-border">
              {[...cfg.history].reverse().slice(0, 15).map((h, i) => (
                <li key={i} className="py-3 flex items-start gap-3">
                  {h.ok ? <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-destructive mt-1 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{h.title || h.topic || (h.ok ? "Published" : "Failed")}</p>
                    <p className="text-[11px] font-mono text-muted-foreground truncate">
                      {(h.ts || "").slice(0, 16).replace("T", " ")} · {h.manual ? "manual" : h.slot}
                      {h.error && <span className="text-destructive"> · {h.error.slice(0, 80)}</span>}
                    </p>
                  </div>
                  {h.slug && <a href={`/blog/${h.slug}`} target="_blank" rel="noreferrer" className="text-primary hover:text-primary/80 flex-shrink-0"><ExternalLink className="w-4 h-4" /></a>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 p-5 bg-primary/10 border border-primary/30 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-foreground mb-1">How it works</p>
          <ol className="list-decimal ml-5 space-y-0.5 text-foreground/80">
            <li>At each scheduled hour (UTC), the AI invents a fresh topic from your niches (avoiding the last 30 titles).</li>
            <li>It writes a 600-900 word post in your voice with 2-3 natural affiliate links from your Recommended list.</li>
            <li>The post is <b>published immediately</b> to <b>/blog</b> with a hero image and today&apos;s date.</li>
            <li>Not happy with a post? Delete it from the Blog tab and it&apos;s gone.</li>
          </ol>
        </div>
      </div>
      <EducationEngine />
      <SkillEngine />
      <ToolBuilder />
      <ToolsSpotlight />
      <FunEngine />
      <FbReplies />
      <AdminAvenoEngine />
    </div>
  );
}

function EducationEngine() {
  const [edu, setEdu] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => educationStatus().then(setEdu).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!edu) return null;

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); if (r && r.ok === false) toast.error(r.error || "Failed"); else toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="education-engine" className="mt-8 border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="w-6 h-6 text-emerald-700" />
        <h3 className="font-display text-2xl">Education — Literacy in Secondary Schools</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        A warm, humanistic daily series on literacy in grades 6-12 — US-focused with international lessons.
        Every article gets a warm editorial photo, publishes to the blog's Education section, and
        {" "}every article is shared to LinkedIn + Facebook (paced through the day). Two of the seven daily posts
        are writing-tips articles (academic, business, creative and more). This runs in addition to the auto blog above.
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="education-toggle"
          onClick={() => run("toggle", () => educationToggle({ enabled: !edu.enabled }), edu.enabled ? "Series paused" : "Series running")}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${edu.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
          {edu.enabled ? "On" : "Off"}
        </button>
        <span className="text-sm text-muted-foreground">{edu.per_day}/day · all shared to social · 2 writing-tips</span>
        <button data-testid="education-generate-btn" disabled={busy === "gen"}
          onClick={() => run("gen", () => educationGenerate("", true), "Writing one now — ready in about a minute")}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Write one now
        </button>
        {!edu.seeded && (
          <button data-testid="education-seed-btn" disabled={busy === "seed" || edu.seed_running}
            onClick={() => { if (window.confirm("Generate the first 10 literacy articles now? The first 5 will be shared to LinkedIn + Facebook, paced 45 min apart. Takes ~15 minutes.")) run("seed", educationSeed, "Seeding the first 10 — watch the Education section"); }}
            className="inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 px-4 py-2 text-sm font-semibold hover:bg-emerald-50 transition disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> {edu.seed_running ? "Seeding…" : "Seed the first 10"}
          </button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4" data-testid="education-today">
        Today: <strong>{edu.today?.published ?? 0}</strong>/{edu.per_day} published · <strong>{edu.today?.shared ?? 0}</strong>/{edu.social_per_day} shared · {edu.total} articles total
      </p>
      {(edu.recent || []).length > 0 && (
        <div className="space-y-2">
          {edu.recent.map((r, i) => (
            <div key={r.slug || i} data-testid={`education-item-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-border pb-2 last:border-0">
              <span className="truncate font-medium">{r.title}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                {r.hero_image && <span className="text-emerald-700">photo</span>}
                <a href={`/blog/${r.slug}`} target="_blank" rel="noreferrer" className="text-primary underline">read</a>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ToolsSpotlight() {
  const [ts, setTs] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => toolsEngineStatus().then(setTs).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!ts) return null;

  return (
    <div data-testid="tools-engine" className="mt-8 border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-2">
        <Wrench className="w-6 h-6 text-primary" />
        <h3 className="font-display text-2xl">Free Tools Spotlight — social series</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        {ts.per_day} social posts per day, each spotlighting one of your free tools: what it's for, the typical business wins,
        creative angles, and 3-5 concrete examples — with the link to try it. Image posts (no video), shared to your connected networks.
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="tools-toggle"
          onClick={async () => { setBusy(true); try { await toolsEngineToggle({ enabled: !ts.enabled }); toast.success(ts.enabled ? "Paused" : "Running"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${ts.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
          {ts.enabled ? "On" : "Off"}
        </button>
        <span className="text-sm text-muted-foreground">tool {Math.min((ts.cursor ?? 0) + 1, ts.tools_total ?? 0)} of {ts.tools_total ?? "…"} in rotation</span>
        <button data-testid="tools-run-now-btn" disabled={busy}
          onClick={async () => { setBusy(true); try { await toolsEngineRunNow(); toast.success("Spotlighting a tool now — check Recent shares in a few minutes"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Spotlight a tool now
        </button>
      </div>
      <p className="text-sm text-muted-foreground" data-testid="tools-today">Today: <strong>{ts.today?.published ?? 0}</strong>/{ts.per_day} spotlight posts</p>
    </div>
  );
}
function SkillEngine() {
  const [sk, setSk] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => skillEngineStatus().then(setSk).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!sk) return null;

  return (
    <div data-testid="skill-engine" className="mt-8 border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-2">
        <Briefcase className="w-6 h-6 text-amber-700" />
        <h3 className="font-display text-2xl">Skill to Business — side-hustle guides</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        {sk.per_day} guides per day, each showing readers how to turn one specific skill into a side-hustle or
        full-time income: the legal paperwork and business license in plain English, building the website and
        social pages, marketing that works for that skill, and creative monetization routes. Publishes to the
        blog's Skill to Business section and every guide is shared to LinkedIn + Facebook.
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="skill-toggle"
          onClick={async () => { setBusy(true); try { await skillEngineToggle({ enabled: !sk.enabled }); toast.success(sk.enabled ? "Paused" : "Running"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${sk.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
          {sk.enabled ? "On" : "Off"}
        </button>
        <span className="text-sm text-muted-foreground">{sk.per_day}/day · all shared to social</span>
        <button data-testid="skill-run-now-btn" disabled={busy}
          onClick={async () => { setBusy(true); try { await skillEngineRunNow(); toast.success("Writing a guide now — ready in about a minute"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Write one now
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-4" data-testid="skill-today">
        Today: <strong>{sk.published_today ?? 0}</strong>/{sk.per_day} published · {sk.total} guides total
      </p>
      {(sk.recent || []).length > 0 && (
        <div className="space-y-2">
          {sk.recent.map((r, i) => (
            <div key={r.slug || i} data-testid={`skill-item-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-border pb-2 last:border-0">
              <span className="truncate font-medium">{r.title}</span>
              <a href={`/blog/${r.slug}`} target="_blank" rel="noreferrer" className="text-primary underline text-xs flex-shrink-0">read</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ToolBuilder() {
  const [tb, setTb] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => toolBuilderStatus().then(setTb).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!tb) return null;

  return (
    <div data-testid="tool-builder" className="mt-8 border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-2">
        <Hammer className="w-6 h-6 text-primary" />
        <h3 className="font-display text-2xl">Tool Builder — 2 new tools a day</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        The AI designs {tb.per_day} new free tools every day (calculators and scorecards), validates the spec,
        and ships them straight into the Tools section — no redeploy needed. Each new tool is announced with a
        launch spotlight on your social pages and joins the spotlight rotation.
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="tool-builder-toggle"
          onClick={async () => { setBusy(true); try { await toolBuilderToggle({ enabled: !tb.enabled }); toast.success(tb.enabled ? "Paused" : "Running"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${tb.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
          {tb.enabled ? "On" : "Off"}
        </button>
        <span className="text-sm text-muted-foreground">{tb.tools_total ?? "…"} tools live ({tb.total_generated ?? 0} AI-built)</span>
        <button data-testid="tool-builder-run-now-btn" disabled={busy}
          onClick={async () => { setBusy(true); try { await toolBuilderRunNow(); toast.success("Designing a new tool — live in about a minute"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Build one now
        </button>
      </div>
      {(tb.recent || []).length > 0 && (
        <div className="space-y-2">
          {tb.recent.map((r, i) => (
            <div key={r.slug || i} data-testid={`tool-builder-item-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-border pb-2 last:border-0">
              <span className="truncate font-medium">{r.name}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                <span>{r.kind}</span>
                <a href={`/tools/${r.slug}`} target="_blank" rel="noreferrer" className="text-primary underline">open</a>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FunEngine() {
  const [fe, setFe] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => funEngineStatus().then(setFe).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!fe) return null;

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); if (r && r.ok === false) toast.error(r.error || "Failed"); else toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="fun-engine" className="mt-8 border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-2">
        <Laugh className="w-6 h-6 text-pink-600" />
        <h3 className="font-display text-2xl">Facebook Fun — memes & reels</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        {fe.memes_per_day} funny memes and {fe.reels_per_day} comedic vertical reels per day, each riffing on a recent
        blog post or tool — posted to Facebook AND LinkedIn (LinkedIn gets an educational mini-lesson version).
        Every post links back to the article it riffs on, with trending + topic hashtags.
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="fun-toggle"
          onClick={() => run("toggle", () => funEngineToggle({ enabled: !fe.enabled }), fe.enabled ? "Paused" : "Running")}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${fe.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
          {fe.enabled ? "On" : "Off"}
        </button>
        <span className="text-sm text-muted-foreground">{fe.memes_per_day} memes + {fe.reels_per_day} reels/day · Facebook + LinkedIn</span>
        <button data-testid="fun-meme-btn" disabled={!!busy}
          onClick={() => run("meme", () => funEngineRunNow("meme"), "Making a meme — ready in about a minute")}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Make a meme
        </button>
        <button data-testid="fun-reel-btn" disabled={!!busy}
          onClick={() => run("reel", () => funEngineRunNow("reel"), "Reel queued — the worker builds the video in the background (a few minutes)")}
          className="inline-flex items-center gap-2 border border-primary text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Roll a reel
        </button>
        <button data-testid="fun-boost-all-btn" disabled={!!busy}
          onClick={() => run("boost", async () => { const r = await funEngineBoostAll(); if (r.ok) toast.success(`LinkedIn companions queued: ${r.boosted} boosted, ${r.skipped_existing} already had one`); return r; }, "Boosting memes to LinkedIn…")}
          className="inline-flex items-center gap-2 border border-border text-muted-foreground px-4 py-2 text-sm font-semibold hover:bg-muted transition disabled:opacity-50">
          <Play className="w-4 h-4" /> Send all memes to LinkedIn
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-4" data-testid="fun-today">
        Today: <strong>{fe.memes_today ?? 0}</strong>/{fe.memes_per_day} memes · <strong>{fe.reels_today ?? 0}</strong>/{fe.reels_per_day} reels
      </p>
      {(fe.recent || []).length > 0 && (
        <div className="space-y-2">
          {fe.recent.map((r, i) => (
            <div key={r.slug || i} data-testid={`fun-item-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-border pb-2 last:border-0">
              <span className="truncate font-medium">{r.slug}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                <span className={r.kind === "reel" ? "text-pink-600" : "text-amber-700"}>{r.kind}</span>
                <span className={r.status === "error" ? "text-red-600 font-semibold" : ""}>
                  {r.status}{r.work_attempts ? ` ·try ${r.work_attempts}` : ""}
                </span>
                {r.detail && <span data-testid={`fun-item-detail-${i}`} className="text-red-600 max-w-[220px] truncate" title={r.detail}>{r.detail}</span>}
                {r.media_url && <a href={r.media_url} target="_blank" rel="noreferrer" className="text-primary underline">view</a>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FbReplies() {
  const [fr, setFr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => fbRepliesStatus().then(setFr).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  if (!fr) return null;

  return (
    <div data-testid="fb-replies" className="mt-8 border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-2">
        <MessageCircle className="w-6 h-6 text-blue-600" />
        <h3 className="font-display text-2xl">Facebook Comment Replies</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        Scans comments on your Facebook posts and, when someone is asking about a service you offer or a problem
        one of your free tools solves, replies as your Page with a warm recommendation and the link
        (max {fr.per_day}/day). Note: Facebook's API only allows reading comments on your own page.
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="fb-replies-toggle" disabled={busy}
          onClick={async () => { setBusy(true); try { await fbRepliesToggle({ enabled: !fr.enabled }); toast.success(fr.enabled ? "Paused" : "Running"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } }}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${fr.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
          {fr.enabled ? "On" : "Off"}
        </button>
        <span className="text-sm text-muted-foreground" data-testid="fb-replies-today">
          Today: <strong>{fr.replied_today ?? 0}</strong>/{fr.per_day} replies · {fr.total_replied ?? 0} total
        </span>
      </div>
      {(fr.recent || []).length > 0 && (
        <div className="space-y-2">
          {fr.recent.map((r, i) => (
            <div key={i} data-testid={`fb-reply-item-${i}`} className="text-sm border-b border-border pb-2 last:border-0">
              <p className="truncate text-muted-foreground">{r.reply}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
